import * as React from "react";
import { useState, useEffect, useCallback, useRef } from "react";
import {
  makeStyles,
  shorthands,
  tokens,
  Button,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  Card,
  CardHeader,
  Text,
  Badge,
  Spinner,
  Divider,
  Avatar,
  Tooltip,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  MessageBarActions,
} from "@fluentui/react-components";
import {
  MailRegular,
  PersonRegular,
  SendRegular,
  CheckmarkCircleRegular,
  DismissCircleRegular,
  DocumentRegular,
  ArrowSyncRegular,
  ShieldCheckmarkRegular,
  SignOutRegular,
} from "@fluentui/react-icons";
import { useAuth } from "../../auth/useAuth";
import { userValidationService, ValidationResult } from "../services/userValidation";
import LoadingPage from "./LoadingPage";
import ContactSupportPage from "./ContactSupportPage";
import AccessDeniedPage from "./AccessDeniedPage";
import TenderSelectionPage from "./TenderSelectionPage";
import EmailSubmissionForm from "./EmailSubmissionForm";
import LetterTimeline from "./LetterTimeline";
import LetterPreview from "./LetterPreview";
import ConversationPrompt from "./ConversationPrompt";
import { Tender, EmailFormData } from "../types/tender";
import { checkConversation, ConversationLetter } from "../services/conversationService";
import { threadService } from "../services/threadService";

/* global Office */

interface EmailData {
  subject: string;
  senderName: string;
  senderEmail: string;
  bodyHtml: string;
  receivedDateTime?: string;
  conversationId?: string;
  internetMessageId?: string;
  isSentFolder?: boolean;
  outlookUserEmail?: string; // Email of the user logged into Outlook
  recipientEmail?: string; // Email of the recipient (for sent) or Outlook user (for received)
  recipientName?: string; // Name of the recipient (for sent) or "Me" (for received)
}

interface AppProps {
  title?: string;
}

const normalizeInternetMessageId = (value?: string): string => {
  if (!value) return "";
  return String(value).trim().replace(/^<+|>+$/g, "").toLowerCase();
};

const normalizeConversationId = (value?: string): string => {
  if (!value) return "";
  return String(value).trim();
};

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    backgroundColor: "#FFFFFF",
    fontFamily: "'Source Sans 3', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    overflow: "hidden", // Prevent horizontal scroll in narrow viewport
  },
  header: {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    justifyContent: "center",
    ...shorthands.gap("0px"), // Reset gap
    ...shorthands.padding("8px", "16px"),
    backgroundColor: "#FFFFFF",
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    position: "sticky",
    top: 0,
    zIndex: 100,
    minHeight: "44px",
    flexShrink: 0,
  },
  headerTopRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
  },
  logoContainer: {
    display: "flex",
    alignItems: "center",
    ...shorthands.gap("10px"),
    flexShrink: 0,
  },
  logoIcon: {
    width: "32px",
    height: "32px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  logoImg: {
    width: "32px",
    height: "32px",
    objectFit: "contain",
  },
  logoText: {
    fontSize: "16px", // Reduced for narrow viewport
    fontWeight: "800",
    color: "#0B5D5D",
    fontFamily: "'JetBrains Mono', monospace", // Distinctive heading font
    letterSpacing: "-0.5px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  statusBadge: {
    display: "flex",
    alignItems: "center",
    ...shorthands.gap("4px"),
    fontSize: "10px",
    color: "#0B5D5D",
    backgroundColor: "#E6F4F4", // Updated for #0B5D5D
    ...shorthands.padding("3px", "8px"),
    ...shorthands.borderRadius("10px"),
    fontWeight: "600",
    border: "1px solid #0B5D5D",
    flexShrink: 0,
    whiteSpace: "nowrap",
  },
  profileMenuButton: {
    minWidth: "auto",
    ...shorthands.padding("2px"),
    height: "28px",
    display: "flex",
    alignItems: "center",
  },
  content: {
    flexGrow: 1,
    ...shorthands.padding("16px"), // Reduced padding for narrow viewport
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap("16px"), // Tighter spacing
    overflowY: "auto",
    overflowX: "hidden", // Prevent horizontal scroll
    minHeight: 0, // Allow flex shrinking
  },
  emailCard: {
    backgroundColor: "#FFFFFF",
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
    ...shorthands.borderRadius("8px"), // Slightly smaller radius for tighter space
    boxShadow: "0 2px 8px rgba(11, 93, 93, 0.08), 0 1px 3px rgba(0, 0, 0, 0.06)", // Branded shadow
    ...shorthands.overflow("hidden"),
    transition: "box-shadow 0.2s ease-in-out", // Subtle motion
    ":hover": {
      boxShadow: "0 4px 12px rgba(11, 93, 93, 0.12), 0 2px 6px rgba(0, 0, 0, 0.08)",
    },
  },
  cardSection: {
    ...shorthands.padding("14px", "16px"), // Reduced padding for narrow viewport
  },
  cardSectionWithBorder: {
    ...shorthands.padding("14px", "16px"),
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  sectionLabel: {
    fontSize: "10px",
    fontWeight: "800", // Bolder as requested
    fontFamily: "'JetBrains Mono', monospace", // Distinctive heading font
    textTransform: "uppercase" as const,
    letterSpacing: "0.8px",
    color: "#0B5D5D", // Updated brand color
    marginBottom: "10px",
    display: "flex",
    alignItems: "center",
    ...shorthands.gap("6px"),
  },
  subjectText: {
    fontSize: "15px", // Slightly smaller for narrow viewport
    fontWeight: "600",
    color: "#1A1A1A",
    lineHeight: "1.45",
    wordBreak: "break-word" as const,
    hyphens: "auto", // Better text wrapping
  },
  senderContainer: {
    display: "flex",
    alignItems: "center",
    ...shorthands.gap("10px"),
  },
  senderInfo: {
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap("3px"),
    minWidth: 0, // Allow text truncation
    flex: 1,
  },
  senderName: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#1A1A1A",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    minWidth: 0,
    display: "block",
  },
  senderEmail: {
    fontSize: "12px",
    color: tokens.colorNeutralForeground3,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    minWidth: 0,
    display: "block",
  },
  bodyPreview: {
    maxHeight: "200px", // Increased height for better content visibility
    overflowY: "auto",
    overflowX: "auto", // Allow horizontal scrolling if needed
    backgroundColor: "#FDFDFD", // Subtle white-adjacent tint
    ...shorthands.borderRadius("6px"),
    ...shorthands.padding("12px", "14px"),
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
    fontSize: "12px",
    lineHeight: "1.55",
    color: "#333333",
    wordBreak: "break-word",
    overflowWrap: "break-word",
  },
  bodyHtmlContainer: {
    width: "100%",
    minHeight: "auto",
    "& img": {
      maxWidth: "100%",
      height: "auto",
    },
    "& a": {
      color: "#0B5D5D",
    },
    "& *": {
      maxWidth: "100%",
      wordBreak: "break-word" as const,
      boxSizing: "border-box" as const,
    },
    "& table": {
      maxWidth: "100%",
      tableLayout: "fixed" as const,
      wordWrap: "break-word" as const,
    },
    "& td, & th": {
      wordBreak: "break-word" as const,
      overflowWrap: "break-word" as const,
    },
  },
  footer: {
    ...shorthands.padding("14px", "16px"),
    backgroundColor: "#FFFFFF",
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap("10px"),
    flexShrink: 0,
  },
  actionButton: {
    height: "42px", // Slightly smaller for narrow viewport
    fontSize: "13px",
    fontWeight: "600",
    ...shorthands.borderRadius("6px"),
    backgroundColor: "#0B5D5D",
    transition: "all 0.2s ease-in-out",
    ":hover": {
      backgroundColor: "#094a4a",
      transform: "translateY(-1px)",
      boxShadow: "0 4px 8px rgba(11, 93, 93, 0.2)",
    },
    ":active": {
      transform: "translateY(0)",
    },
  },
  secondaryButton: {
    height: "42px",
    fontSize: "13px",
    fontWeight: "500",
    ...shorthands.borderRadius("6px"),
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke1),
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "240px", // Reduced for narrow viewport
    ...shorthands.gap("14px"),
    ...shorthands.padding("20px", "16px"),
  },
  loadingText: {
    fontSize: "13px",
    color: tokens.colorNeutralForeground3,
    textAlign: "center",
  },
  errorContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "240px", // Reduced for narrow viewport
    ...shorthands.padding("24px", "16px"),
    textAlign: "center",
  },
  errorIcon: {
    fontSize: "40px", // Smaller for narrow viewport
    color: tokens.colorPaletteRedForeground1,
    marginBottom: "14px",
  },
  errorTitle: {
    fontSize: "16px", // Smaller for narrow viewport
    fontWeight: "600",
    fontFamily: "'JetBrains Mono', monospace",
    color: "#1A1A1A",
    marginBottom: "8px",
    letterSpacing: "-0.2px",
  },
  errorMessage: {
    fontSize: "13px",
    color: tokens.colorNeutralForeground3,
    marginBottom: "18px",
    lineHeight: "1.5",
    wordBreak: "break-word",
  },
  successMessage: {
    backgroundColor: "#E6F4F4",
    ...shorthands.border("1px", "solid", "#57C1C1"),
    ...shorthands.borderRadius("6px"),
    ...shorthands.padding("10px", "14px"),
    display: "flex",
    alignItems: "center",
    ...shorthands.gap("10px"),
  },
  successIcon: {
    color: "#57C1C1",
    fontSize: "18px",
    flexShrink: 0,
  },
  successText: {
    fontSize: "13px",
    fontWeight: "500",
    color: "#0B5D5D",
    wordBreak: "break-word",
  },
  notInOutlookContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "100vh",
    ...shorthands.padding("32px", "16px"), // Reduced padding for narrow viewport
    textAlign: "center",
    backgroundColor: "#FFFFFF",
  },
  notInOutlookIcon: {
    width: "64px", // Smaller for narrow viewport
    height: "64px",
    backgroundColor: "#F5F5F5",
    ...shorthands.borderRadius("50%"),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "20px",
  },
  statsRow: {
    display: "flex",
    ...shorthands.gap("8px"),
    marginTop: "8px",
    flexWrap: "wrap", // Allow wrapping in narrow viewport
  },
  statBadge: {
    display: "flex",
    alignItems: "center",
    ...shorthands.gap("3px"),
    fontSize: "11px",
    color: tokens.colorNeutralForeground3,
    backgroundColor: "#F8F8F8",
    ...shorthands.padding("3px", "8px"),
    ...shorthands.borderRadius("12px"),
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  authContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center", // Center horizontally
    ...shorthands.gap("6px"),
    minWidth: 0, // Allow shrinking
    flexShrink: 1,
    marginTop: "8px", // Space from logo row
    width: "100%", // Full width for centering
  },
  userInfo: {
    display: "flex",
    alignItems: "center",
    ...shorthands.gap("6px"),
    fontSize: "11px",
    color: "#0B5D5D",
    minWidth: 0, // Allow shrinking
    flexShrink: 1,
    maxWidth: "120px", // Limit width in narrow viewport
    overflow: "hidden",
    "& > span": {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
  },
  signOutButton: {
    minWidth: "auto",
    ...shorthands.padding("3px", "6px"),
    height: "26px",
    flexShrink: 0,
  },
  loginCard: {
    backgroundColor: "#FFFFFF",
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
    ...shorthands.borderRadius("8px"),
    boxShadow: "0 2px 8px rgba(11, 93, 93, 0.08), 0 1px 3px rgba(0, 0, 0, 0.06)",
    ...shorthands.padding("20px", "16px"), // Reduced padding for narrow viewport
    textAlign: "center",
    maxWidth: "100%", // Full width in narrow viewport
    margin: "auto",
  },
  loginIcon: {
    fontSize: "40px", // Smaller for narrow viewport
    color: "#0B5D5D",
    marginBottom: "14px",
    display: "block",
    marginLeft: "auto",
    marginRight: "auto",
  },
  loginTitle: {
    fontSize: "15px", // Made smaller as requested
    fontWeight: "700",
    fontFamily: "'JetBrains Mono', monospace", // Distinctive heading font
    color: "#1A1A1A",
    marginBottom: "8px",
    letterSpacing: "-0.3px",
    display: "block",
    textAlign: "center",
  },
  loginDescription: {
    fontSize: "12px",
    color: tokens.colorNeutralForeground3,
    marginTop: "16px",
    marginBottom: "16px",
    lineHeight: "1.4",
    textAlign: "center",
    display: "-webkit-box",
    WebkitLineClamp: "2",
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    ...shorthands.padding("0", "8px"),
  },
  loginButton: {
    height: "42px",
    fontSize: "13px",
    fontWeight: "600",
    ...shorthands.borderRadius("6px"),
    backgroundColor: "#0B5D5D",
    transition: "all 0.2s ease-in-out",
    ":hover": {
      backgroundColor: "#094a4a",
      transform: "translateY(-1px)",
      boxShadow: "0 4px 8px rgba(11, 93, 93, 0.2)",
    },
  },
});

const App: React.FC<AppProps> = () => {
  const styles = useStyles();
  const { user, isAuthenticated, isLoading: authLoading, error: authError, signIn, signOut, getAccessToken } = useAuth();
  const [emailData, setEmailData] = useState<EmailData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [sendSuccess, setSendSuccess] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [threadMetadataError, setThreadMetadataError] = useState<string | null>(null);
  const [isInOutlook, setIsInOutlook] = useState(false);

  // User validation state
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [validationComplete, setValidationComplete] = useState(false);

  // Tender selection state
  const [selectedTender, setSelectedTender] = useState<Tender | null>(null);
  const [showTenderSelection, setShowTenderSelection] = useState(false);
  const [submissionFormData, setSubmissionFormData] = useState<EmailFormData | null>(null);

  // Conversation state
  const [isCheckingConversation, setIsCheckingConversation] = useState(false);
  const [conversationLetters, setConversationLetters] = useState<ConversationLetter[]>([]);
  const [isNewConversation, setIsNewConversation] = useState(true);
  const [isDuplicateMessage, setIsDuplicateMessage] = useState(false);
  const [existingLetter, setExistingLetter] = useState<any>(null);
  const [conversationCheckComplete, setConversationCheckComplete] = useState(false);
  const [timelineRefreshKey, setTimelineRefreshKey] = useState(0);

  // Ref for scrolling to submission form
  const submissionFormRef = useRef<HTMLDivElement>(null);

  /**
   * Extract only the current email body, removing quoted/replied content from thread
   * @param html - Full email HTML including thread history
   * @returns HTML of only the current message
   */
  const extractCurrentEmailBody = (html: string): string => {
    const tmp = document.createElement("div");
    tmp.innerHTML = html;

    // Remove Gmail-style quoted text (div class="gmail_quote")
    const gmailQuotes = tmp.querySelectorAll('.gmail_quote');
    gmailQuotes.forEach(quote => quote.remove());

    // Remove Outlook-style quoted text (div class contains "OutlookMessageHeader" or "quoted")
    const outlookQuotes = tmp.querySelectorAll('[class*="OutlookMessageHeader"], [class*="quoted"], [class*="quote"]');
    outlookQuotes.forEach(quote => quote.remove());

    // Remove all blockquote elements (standard quoted text)
    const blockquotes = tmp.querySelectorAll('blockquote');
    blockquotes.forEach(quote => quote.remove());

    // Get the remaining HTML
    let cleanedHtml = tmp.innerHTML;

    // Remove content after common email reply patterns using regex
    const replyPatterns = [
      /On .+? wrote:/i,           // "On Tue, 20 Jan 2026, 14:07 Person wrote:"
      /From:.+?Sent:.+?To:/is,    // Outlook forward/reply header
      /_{5,}/,                     // Long underscores often separate replies
      /-----Original Message-----/i,
      /________________________________/,  // Outlook separator
    ];

    for (const pattern of replyPatterns) {
      const match = cleanedHtml.match(pattern);
      if (match && match.index !== undefined) {
        cleanedHtml = cleanedHtml.substring(0, match.index);
        break; // Stop at first match
      }
    }

    return cleanedHtml;
  };

  const loadEmailData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setSendSuccess(false);
    setSendError(null);

    try {
      if (!Office.context.mailbox || !Office.context.mailbox.item) {
        throw new Error("No email is currently selected. Please open an email to use this add-in.");
      }

      const item = Office.context.mailbox.item;

      const subject = item.subject || "No Subject";
      const senderName = item.sender?.displayName || item.from?.displayName || "Unknown Sender";
      const senderEmail = item.sender?.emailAddress || item.from?.emailAddress || "unknown@email.com";

      // Get conversation and message IDs for thread tracking
      const conversationId = normalizeConversationId(item.conversationId);
      const internetMessageId = normalizeInternetMessageId(item.internetMessageId);

      // Detect if email is in Sent folder by checking if sender matches Outlook account
      let isSentFolder = false;
      let outlookUserEmail = "";
      let recipientEmail = "";
      let recipientName = "";

      try {
        // IMPORTANT: Use Outlook account email, NOT add-in login email
        // Office.context.mailbox.userProfile.emailAddress = email logged into Outlook
        // user?.email = email used to login to the add-in (could be different!)
        outlookUserEmail = Office.context.mailbox.userProfile.emailAddress;

        if (outlookUserEmail && senderEmail &&
          senderEmail.toLowerCase() === outlookUserEmail.toLowerCase()) {
          isSentFolder = true;
        }

        console.log('[APP] Folder detection:', {
          senderEmail,
          outlookUserEmail,
          addinLoginEmail: user?.email,
          isSentFolder
        });
      } catch (error) {
        console.log('[APP] Could not detect folder, defaulting to Received');
      }

      // Extract the actual email's creation date from Outlook
      // dateTimeCreated represents when the email was sent/created
      // This is the only reliable date property available in Office.js API
      let receivedDateTime: string;
      try {
        const emailDate = item.dateTimeCreated;
        if (emailDate) {
          receivedDateTime = new Date(emailDate).toISOString();
          console.log('[APP] Email date from Outlook (dateTimeCreated):', receivedDateTime);
        } else {
          receivedDateTime = new Date().toISOString();
          console.warn('[APP] FALLBACK: dateTimeCreated is undefined, using current timestamp');
        }
      } catch (error) {
        receivedDateTime = new Date().toISOString();
        console.error('[APP] FALLBACK: Error reading dateTimeCreated:', error);
      }

      // Get recipient information based on email direction
      try {
        if (isSentFolder) {
          // For sent emails: get recipient from the "to" field
          const recipients = item.to;
          if (recipients && recipients.length > 0) {
            recipientEmail = recipients[0].emailAddress;
            recipientName = recipients[0].displayName || recipientEmail;
            console.log('[APP] Sent email recipient:', {
              recipientName,
              recipientEmail
            });
          } else {
            // Fallback if no recipients found
            recipientEmail = outlookUserEmail;
            recipientName = "Unknown Recipient";
          }
        } else {
          // For received emails: recipient is the Outlook user (person who received it)
          recipientEmail = outlookUserEmail;
          recipientName = "Me"; // Generic recipient label for received emails
          console.log('[APP] Received email - recipient is Outlook user');
        }
      } catch (error) {
        console.log('[APP] Could not extract recipient info:', error);
        recipientEmail = outlookUserEmail;
        recipientName = "Unknown Recipient";
      }

      console.log('[APP] Email metadata:', {
        conversationId: conversationId?.substring(0, 20) + '...',
        internetMessageId,
        isSentFolder
      });

      if (!conversationId || !internetMessageId) {
        console.warn('[APP] Missing required thread metadata for duplicate protection', {
          conversationIdPresent: !!conversationId,
          internetMessageIdPresent: !!internetMessageId,
          isSentFolder
        });
        setThreadMetadataError(
          "Cannot verify duplicates for this message because Outlook did not provide conversation/message IDs. Please refresh this email and try again."
        );
      } else {
        setThreadMetadataError(null);
      }

      const fullBodyHtml = await new Promise<string>((resolve, reject) => {
        item.body.getAsync(Office.CoercionType.Html, (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            resolve(result.value);
          } else {
            reject(new Error(result.error?.message || "Failed to get email body"));
          }
        });
      });

      // Extract only the current email body, removing thread/quoted content
      const bodyHtml = extractCurrentEmailBody(fullBodyHtml);

      console.log('[APP] Email body extracted:', {
        fullBodyLength: fullBodyHtml.length,
        extractedBodyLength: bodyHtml.length,
        reduction: `${Math.round((1 - bodyHtml.length / fullBodyHtml.length) * 100)}%`
      });

      setEmailData({
        subject,
        senderName,
        senderEmail,
        bodyHtml,
        receivedDateTime,
        conversationId,
        internetMessageId,
        isSentFolder,
        outlookUserEmail,
        recipientEmail,
        recipientName,
      });

      // Trigger tender selection screen after email data is loaded
      setShowTenderSelection(true);
      console.log('[APP] Email data loaded, showing tender selection');
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load email data");
    } finally {
      setIsLoading(false);
    }
  }, []);

  // User validation function
  const validateUserAccess = useCallback(async (userEmail: string) => {
    console.log(`[APP] 🚀 Starting user validation process for: ${userEmail}`);
    setIsValidating(true);
    setValidationResult(null);

    try {
      console.log(`[APP] 📞 Calling validation service...`);
      const result = await userValidationService.validateUserAccess(userEmail);
      console.log(`[APP] ✅ Validation service returned:`, result);
      setValidationResult(result);
    } catch (error) {
      console.error(`[APP] ❌ Validation error for ${userEmail}:`, error);
      setValidationResult({
        success: false,
        error: 'VALIDATION_ERROR',
        message: 'Failed to validate user access',
        showContactPage: true
      });
    } finally {
      setIsValidating(false);
      setValidationComplete(true);
      console.log(`[APP] 🏁 Validation process completed for: ${userEmail}`);
    }
  }, []);

  // Effect to trigger validation when user is authenticated
  useEffect(() => {
    console.log(`[APP] 🔍 Checking validation trigger conditions:`, {
      isAuthenticated,
      hasUser: !!user,
      userEmail: user?.email,
      validationComplete,
      shouldTrigger: isAuthenticated && user && user.email && !validationComplete
    });

    if (isAuthenticated && user && user.email && !validationComplete) {
      console.log(`[APP] 🎯 Triggering validation for authenticated user: ${user.email}`);
      validateUserAccess(user.email);
    } else {
      console.log(`[APP] ⏸️  Not triggering validation - conditions not met`);
    }
  }, [isAuthenticated, user, validationComplete, validateUserAccess]);

  useEffect(() => {
    const initializeOffice = async () => {
      try {
        await Office.onReady();

        if (Office.context.host === Office.HostType.Outlook) {
          setIsInOutlook(true);
          await loadEmailData();
        } else {
          setIsInOutlook(false);
          setIsLoading(false);
        }
      } catch (err) {
        setIsInOutlook(false);
        setIsLoading(false);
        setError("Failed to initialize Office.js");
      }
    };

    initializeOffice();
  }, [loadEmailData]);

  const handleSendToContraVault = async (formData: EmailFormData) => {
    if (!emailData || !isAuthenticated || !user) {
      setSendError("Please sign in to send email to ContraVault AI");
      return;
    }

    if (!selectedTender) {
      setSendError("Please select a tender before submitting");
      return;
    }

    if (!formData) {
      setSendError("Please fill in the submission form");
      return;
    }

    const normalizedConversationId = normalizeConversationId(emailData.conversationId);
    const normalizedInternetMessageId = normalizeInternetMessageId(emailData.internetMessageId);

    if (threadMetadataError || !normalizedConversationId || !normalizedInternetMessageId) {
      const metadataError =
        threadMetadataError ||
        "Cannot verify duplicates for this message because Outlook did not provide conversation/message IDs. Please refresh this email and try again.";
      setThreadMetadataError(metadataError);
      setSendError(metadataError);
      setSendSuccess(false);
      return;
    }

    // Block submission if this is a duplicate message
    // Button is disabled when isDuplicateMessage is true, so this shouldn't be reached
    // But if it is, just return without setting error (grey warning is already showing)
    if (isDuplicateMessage) {
      return;
    }

    // Pre-submit guard: re-check exact message-id duplicate status right before submit.
    // This catches cases where earlier conversation checks may have been skipped/missed.
    if (emailData.internetMessageId && validationResult?.companyName && selectedTender?.baseCollectionName) {
      try {
        const threadCheck = await threadService.checkThreadDuplicates(
          validationResult.companyName,
          selectedTender.baseCollectionName,
          [emailData.internetMessageId]
        );

        if (threadCheck.data.existingEmails.length > 0) {
          setIsDuplicateMessage(true);
          setExistingLetter(threadCheck.data.existingEmails[0] || null);
          setSendError(null);
          setSendSuccess(false);
          return;
        }
      } catch (threadCheckError) {
        console.error('[APP] Pre-submit duplicate check failed, blocking submission:', threadCheckError);
        setSendError('Could not verify duplicate status. Please try again.');
        setSendSuccess(false);
        return;
      }
    }

    setIsSending(true);
    setSendError(null);
    setSendSuccess(false);

    try {
      const accessToken = await getAccessToken();

      const requestPayload = {
        subject: emailData.subject,
        senderName: emailData.senderName,
        senderEmail: emailData.senderEmail,
        bodyHtml: emailData.bodyHtml,
        timestamp: emailData.receivedDateTime || new Date().toISOString(),
        companyName: validationResult.companyName,
        user: {
          id: user?.id || "unknown",
          email: user?.email || "unknown",
          displayName: user?.displayName || "Unknown",
        },
        selectedTender: {
          id: selectedTender.id,
          title: selectedTender.title,
          baseCollectionName: selectedTender.baseCollectionName,
          contractType: selectedTender.contractType,
          createdAt: selectedTender.createdAt,
        },
        formData: {
          title: formData.title,
          direction: formData.direction,
          letterDate: formData.letterDate,
          deadline: formData.deadline,
          letterTags: formData.letterTags,
          formattedText: formData.formattedText,
        },
        threadMetadata: {
          conversation_id: normalizedConversationId,
          internet_message_id: normalizedInternetMessageId,
        },
      };

      console.log('\n' + '='.repeat(80));
      console.log('[APP] 📤 SUBMITTING EMAIL TO BACKEND');
      console.log('='.repeat(80));
      console.log('[APP] Company:', validationResult.companyName);
      console.log('[APP] Tender:', selectedTender.title);
      console.log('[APP] Letter Title:', formData.title);
      console.log('[APP] Direction:', formData.direction);
      console.log('[APP] Internet Message ID:', emailData.internetMessageId);
      console.log('[APP] Conversation ID:', emailData.conversationId);
      console.log('[APP] Complete Request Payload:', JSON.stringify(requestPayload, null, 2));
      console.log('='.repeat(80) + '\n');

      const response = await fetch("/api/outlook/email", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${accessToken}`,
        },
        body: JSON.stringify(requestPayload),
      });

      if (!response.ok) {
        throw new Error("Failed to send email to ContraVault AI");
      }

      const result = await response.json();

      console.log('\n' + '='.repeat(80));
      console.log('[APP] ✅ SUBMISSION SUCCESSFUL - RESPONSE FROM BACKEND');
      console.log('='.repeat(80));
      console.log('[APP] Success:', result.success);

      if (result.data?.letterFile) {
        console.log('\n[APP] 📁 S3 FILE LOCATION:');
        console.log('[APP] Bucket:', result.data.letterFile.bucket);
        console.log('[APP] S3 Key:', result.data.letterFile.s3Key);
        console.log('[APP] Full S3 Path: s3://' + result.data.letterFile.bucket + '/' + result.data.letterFile.s3Key);
      }

      if (result.data?.letter) {
        console.log('\n[APP] 💾 DYNAMODB ENTRY:');
        console.log('[APP] Letter ID:', result.data.letter.letter_id);
        console.log('[APP] Partition Key (pk):', result.data.letter.pk);
        console.log('[APP] Sort Key (sk):', result.data.letter.sk);
        console.log('[APP] Saved to DynamoDB:', result.data.letter.saved_to_dynamodb);
      }

      console.log('\n[APP] Complete Response:', JSON.stringify(result, null, 2));
      console.log('='.repeat(80) + '\n');

      // Call folder-upload endpoint with the same .txt file
      console.log('\n' + '='.repeat(80));
      console.log('[APP] 📦 CALLING FOLDER-UPLOAD ENDPOINT');
      console.log('='.repeat(80));

      try {
        // Prepare file content (base64 encode the formatted text)
        const textContent = formData.formattedText || '';
        const base64Content = btoa(unescape(encodeURIComponent(textContent)));

        const fileName = `letter_${formData.title.replace(/[^a-zA-Z0-9]/g, '_')}.txt`;

        const folderUploadPayload = {
          companyName: validationResult.companyName,
          contractId: selectedTender.baseCollectionName,
          tenderTitle: selectedTender.title,
          mode: 'direct',
          files: [
            {
              name: fileName,
              content: base64Content,
              title: formData.title,  // Add title here for each file
              tags: formData.letterTags ? formData.letterTags.split(',').map(t => t.trim()) : [],
              sender_name: emailData.senderName,
              sender_email: emailData.senderEmail,
              subject: emailData.subject,
              timestamp: new Date(formData.letterDate).toISOString(),    
              direction: formData.direction,
              letter_id: result.data?.letter?.letter_id,
              uploaded_by: user.email,  // NEW: Required for file_metadata
              deadline: formData.deadline || null,  // NEW: Optional deadline
              conversation_id: normalizedConversationId || null,
              internet_message_id: normalizedInternetMessageId || null,
              received_at: emailData.receivedDateTime || null
            }
          ]
        };

        console.log('[APP] Folder upload payload:', JSON.stringify(folderUploadPayload, null, 2));

        const folderUploadResponse = await fetch("/api/outlook/folder-upload", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(folderUploadPayload),
        });

        if (folderUploadResponse.ok) {
          const folderUploadResult = await folderUploadResponse.json();
          console.log('[APP] ✅ Folder upload successful:', folderUploadResult);
        } else {
          const folderUploadError = await folderUploadResponse.json().catch(() => ({}));

          if (folderUploadResponse.status === 409 || folderUploadError.error === 'DUPLICATE_LETTER') {
            const duplicateEntry = folderUploadError.duplicates?.[0] || null;
            setIsDuplicateMessage(true);
            setExistingLetter(duplicateEntry?.existing || null);
            setSendError(null);
            setSendSuccess(false);
            return;
          }

          throw new Error(folderUploadError.message || `Folder upload failed: HTTP ${folderUploadResponse.status}`);
        }
      } catch (folderUploadError) {
        console.error('[APP] ❌ Folder upload error:', folderUploadError);
        throw folderUploadError;
      }

      console.log('='.repeat(80) + '\n');

      setSendSuccess(true);
      setTimelineRefreshKey((prev) => prev + 1);

      // Refresh conversation state after successful submission
      // This will show the newly submitted letter in the conversation timeline
      if (emailData?.conversationId && emailData?.internetMessageId && validationResult?.companyName && selectedTender?.baseCollectionName) {
        console.log('[APP] Refreshing conversation state after submission...');

        // Wait a brief moment for DynamoDB to be consistent
        await new Promise(resolve => setTimeout(resolve, 1000));

        await checkConversationStatus(
          validationResult.companyName,
          selectedTender.baseCollectionName,
          emailData.conversationId,
          emailData.internetMessageId
        );

        console.log('[APP] Conversation state refreshed');
      }

    } catch (err) {
      setSendError(err instanceof Error ? err.message : "Failed to send email");
    } finally {
      setIsSending(false);
    }
  };

  // Check conversation status
  const checkConversationStatus = async (
    companyName: string,
    baseCollectionName: string,
    conversationId: string,
    internetMessageId: string
  ) => {
    const normalizedConversationId = normalizeConversationId(conversationId);
    const normalizedInternetMessageId = normalizeInternetMessageId(internetMessageId);

    if (!normalizedConversationId || !normalizedInternetMessageId) {
      const metadataError =
        "Cannot verify duplicates for this message because Outlook did not provide conversation/message IDs. Please refresh this email and try again.";
      setThreadMetadataError(metadataError);
      setSendError(metadataError);
      setIsDuplicateMessage(false);
      setExistingLetter(null);
      setConversationCheckComplete(true);
      setIsCheckingConversation(false);
      return;
    }

    setIsCheckingConversation(true);
    setConversationCheckComplete(false);

    try {
      console.log('[APP] Checking conversation status...');
      const result = await checkConversation(
        companyName,
        baseCollectionName,
        normalizedConversationId,
        normalizedInternetMessageId
      );

      let duplicateFromThreadCheck = false;
      let existingFromThreadCheck: any = null;

      // Fallback: if conversation lookup misses, verify exact message-id duplicate directly.
      if (!result.data.isDuplicateMessage && normalizedInternetMessageId) {
        try {
          const threadCheck = await threadService.checkThreadDuplicates(
            companyName,
            baseCollectionName,
            [normalizedInternetMessageId]
          );
          duplicateFromThreadCheck = threadCheck.data.existingEmails.length > 0;
          existingFromThreadCheck = threadCheck.data.existingEmails[0] || null;
        } catch (threadCheckError) {
          console.error('[APP] Thread duplicate fallback check failed:', threadCheckError);
          setSendError('Could not verify duplicate status. Please try again.');
          setConversationCheckComplete(true);
          return;
        }
      }

      console.log('[APP] Conversation check result:', {
        isNewConversation: result.data.isNewConversation,
        isDuplicateMessage: result.data.isDuplicateMessage || duplicateFromThreadCheck,
        letterCount: result.data.letters.length
      });

      setConversationLetters(result.data.letters);
      setIsNewConversation(result.data.isNewConversation);
      setIsDuplicateMessage(result.data.isDuplicateMessage || duplicateFromThreadCheck);
      setExistingLetter(result.data.existingLetter || existingFromThreadCheck || null);
      setConversationCheckComplete(true);

      // If duplicate, the grey warning message will be shown automatically
      // Don't set sendError here - we want grey warning, not red error

    } catch (error) {
      console.error('[APP] Error checking conversation:', error);
      // Fallback to exact message-id duplicate check if conversation endpoint fails.
      try {
        const threadCheck = await threadService.checkThreadDuplicates(
          companyName,
          baseCollectionName,
          [normalizedInternetMessageId]
        );
        const duplicate = threadCheck.data.existingEmails.length > 0;
        setIsDuplicateMessage(duplicate);
        setExistingLetter(threadCheck.data.existingEmails[0] || null);
      } catch (threadCheckError) {
        console.error('[APP] Thread duplicate fallback after conversation error failed:', threadCheckError);
        setSendError('Failed to verify duplicate status. Please refresh and try again.');
      } finally {
        setConversationCheckComplete(true);
      }
    } finally {
      setIsCheckingConversation(false);
    }
  };

  // Handle form submission
  const handleFormSubmit = async (formData: EmailFormData) => {
    console.log('[APP] Form submitted:', formData);
    setSubmissionFormData(formData);
    // Trigger email submission with form data
    await handleSendToContraVault(formData);
  };

  // Handle tender selection
  const handleTenderSelection = async (tender: Tender) => {
    console.log('[APP] Tender selected:', tender);
    setSelectedTender(tender);
    setShowTenderSelection(false);
    setSendError(null);
    setSendSuccess(false);

    // Check conversation after tender selection
    if (emailData?.conversationId && emailData?.internetMessageId && validationResult?.companyName) {
      await checkConversationStatus(
        validationResult.companyName,
        tender.baseCollectionName || tender.id,
        emailData.conversationId,
        emailData.internetMessageId
      );
    } else {
      console.warn('[APP] Missing conversation data, skipping conversation check');
      if (!threadMetadataError) {
        setThreadMetadataError(
          "Cannot verify duplicates for this message because Outlook did not provide conversation/message IDs. Please refresh this email and try again."
        );
      }
      setConversationCheckComplete(true);
    }
  };

  // Handle back to tender selection
  const handleBackToTenderSelection = () => {
    console.log('[APP] Returning to tender selection');
    setSelectedTender(null);
    setSubmissionFormData(null);
    setShowTenderSelection(true);
    setSendSuccess(false);
    setSendError(null);

    // Reset conversation state
    setConversationCheckComplete(false);
    setConversationLetters([]);
    setIsNewConversation(true);
    setIsDuplicateMessage(false);
    setExistingLetter(null);
  };

  // Handle scroll to submission form
  const handleScrollToForm = () => {
    console.log('[APP] Scrolling to submission form');
    submissionFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const getInitials = (name: string): string => {
    return name
      .split(" ")
      .map((part) => part[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const stripHtml = (html: string): string => {
    const tmp = document.createElement("div");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  };

  const formatDate = (isoDate: string): string => {
    try {
      return new Date(isoDate).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return isoDate;
    }
  };

  if (!isInOutlook && !isLoading) {
    return (
      <div className={styles.notInOutlookContainer}>
        <div className={styles.notInOutlookIcon}>
          <MailRegular style={{ fontSize: "36px", color: "#666666" }} />
        </div>
        <Text className={styles.errorTitle}>Open in Outlook</Text>
        <Text className={styles.errorMessage}>
          This add-in works within Microsoft Outlook. Please open an email in Outlook to use ContraVault AI.
        </Text>
        <Badge appearance="outline" color="informative">
          Outlook Add-in
        </Badge>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerTopRow}>
          <div className={styles.logoContainer}>
            <Text className={styles.logoText}>ContraVault AI</Text>
          </div>
          {isAuthenticated && user && (
            <Menu positioning="below-end">
              <MenuTrigger disableButtonEnhancement>
                <Button appearance="subtle" className={styles.profileMenuButton}>
                  <Avatar
                    name={user.displayName}
                    size={20}
                    color="brand"
                  />
                </Button>
              </MenuTrigger>
              <MenuPopover>
                <MenuList>
                  <MenuItem icon={<SignOutRegular />} onClick={signOut}>Logout</MenuItem>
                </MenuList>
              </MenuPopover>
            </Menu>
          )}
        </div>
      </div>

      {/* Content */}
      <div className={styles.content}>
        {authLoading ? (
          <div className={styles.loadingContainer}>
            <Spinner size="large" />
            <Text className={styles.loadingText}>Initializing authentication...</Text>
          </div>
        ) : !isAuthenticated ? (
          <div className={styles.loginCard}>
            <ShieldCheckmarkRegular className={styles.loginIcon} />
            <Text className={styles.loginTitle}>ContraVault AI Add-In <br /> for Outlook</Text>
            <Text className={styles.loginDescription}>
              Manage contractual claims effortlessly. Track compliance from your inbox.
            </Text>

            {authError && (
              <MessageBar intent="error" style={{
                marginBottom: "16px",
                fontSize: "12px",
                lineHeight: "1.4",
                wordBreak: "break-word",
                hyphens: "auto",
                whiteSpace: "normal",
                width: "100%",
                boxSizing: "border-box"
              }}>
                <MessageBarBody>
                  <MessageBarTitle style={{ fontSize: "13px", fontWeight: "600" }}>Authentication Error</MessageBarTitle>
                  <div style={{ marginTop: "4px", wordBreak: "break-word", overflowWrap: "break-word" }}>
                    {authError}
                  </div>
                  {authError.includes("Single-Page Application") && (
                    <div style={{
                      marginTop: "8px",
                      fontSize: "11px",
                      padding: "8px",
                      backgroundColor: "#FFF3CD",
                      borderRadius: "4px",
                      border: "1px solid #FFE066",
                      wordBreak: "break-word",

                      lineHeight: "1.4",
                      whiteSpace: "normal",
                      width: "100%",
                    }}>
                      <strong>Quick Fix:</strong> Go to Azure Portal → App registrations → Your app → Authentication →
                      Remove "Web" platform and add "Single-page application" platform instead.
                    </div>
                  )}
                </MessageBarBody>
              </MessageBar>
            )}
            <Button
              appearance="primary"
              onClick={signIn}
              disabled={authLoading}
              className={styles.loginButton}
            >
              {authLoading ? "Signing in..." : "Sign in with Microsoft"}
            </Button>
          </div>
        ) : isValidating ? (
          // Show loading page during validation
          <LoadingPage message="Verifying Access" userEmail={user?.email} />
        ) : validationResult && !validationResult.success ? (
          // Show appropriate error page based on validation result
          userValidationService.getPageToShow(validationResult) === 'accessDenied' ? (
            <AccessDeniedPage
              companyName={validationResult.companyName || 'Unknown'}
              userEmail={user?.email}
              onRetry={() => {
                setValidationComplete(false);
                if (user?.email) {
                  validateUserAccess(user.email);
                }
              }}
            />
          ) : (
            <ContactSupportPage
              userEmail={user?.email}
              onRetry={() => {
                setValidationComplete(false);
                if (user?.email) {
                  validateUserAccess(user.email);
                }
              }}
            />
          )
        ) : isLoading ? (
          <div className={styles.loadingContainer}>
            <Spinner size="large" />
            <Text className={styles.loadingText}>Loading email...</Text>
          </div>
        ) : error ? (
          <div className={styles.errorContainer}>
            <DismissCircleRegular className={styles.errorIcon} />
            <Text className={styles.errorTitle}>Unable to Load Email</Text>
            <Text className={styles.errorMessage}>{error}</Text>
            <Button
              appearance="secondary"
              icon={<ArrowSyncRegular />}
              onClick={loadEmailData}
              className={styles.secondaryButton}
            >
              Try Again
            </Button>
          </div>
        ) : emailData && !isValidating && validationResult?.success && validationResult?.companyName ? (
          <>
            {/* Warning for already submitted email (greyed out) - ONLY AT TOP */}
            {isDuplicateMessage && !sendSuccess && !sendError && (
              <MessageBar intent="warning" style={{
                fontSize: "12px",
                lineHeight: "1.4",
                wordBreak: "break-word",
                whiteSpace: "normal",
                width: "100%",
                backgroundColor: "#f5f5f5",
                borderColor: "#ccc",
                marginBottom: "12px"
              }}>
                <MessageBarBody>
                  <div style={{ color: "#666" }}>This email has already been submitted.</div>
                </MessageBarBody>
              </MessageBar>
            )}

            {/* Success Message - only if success and no error */}
            {sendSuccess && !sendError && (
              <MessageBar style={{
                fontSize: "12px",
                lineHeight: "1.4",
                wordBreak: "break-word",
                whiteSpace: "normal",
                width: "100%",
                backgroundColor: "#E6F4F4",
                borderColor: "#57C1C1"
              }}>
                <MessageBarBody style={{ color: "#0B5D5D" }}>
                  <MessageBarTitle style={{ fontSize: "13px", fontWeight: "600", color: "#0B5D5D" }}>Success</MessageBarTitle>
                  <div style={{ marginTop: "2px" }}>Email sent to ContraVault AI successfully.</div>
                </MessageBarBody>
              </MessageBar>
            )}

            {/* Error Message - only if error and no success */}
            {sendError && !sendSuccess && (
              <MessageBar intent="error" style={{
                fontSize: "12px",
                lineHeight: "1.4",
                wordBreak: "break-word",
                whiteSpace: "normal",
                width: "100%"
              }}>
                <MessageBarBody>
                  <MessageBarTitle style={{ fontSize: "13px", fontWeight: "600" }}>Error</MessageBarTitle>
                  <div style={{ marginTop: "2px", wordBreak: "break-word", overflowWrap: "break-word" }}>
                    {sendError}
                  </div>
                </MessageBarBody>
                <MessageBarActions>
                  <Button
                    appearance="transparent"
                    onClick={() => setSendError(null)}
                    style={{ fontSize: "11px", padding: "4px 8px" }}
                  >
                    Dismiss
                  </Button>
                </MessageBarActions>
              </MessageBar>
            )}

            {/* Step 1: Tender Selection */}
            {showTenderSelection && !selectedTender && (
              <TenderSelectionPage
                companyName={validationResult.companyName}
                onTenderSelected={handleTenderSelection}
              />
            )}

            {/* Step 2: Email Preview + Submission Form */}
            {selectedTender && (
              <>
                {/* Selected Tender Header */}
                <Card className={styles.emailCard} style={{ marginBottom: "12px" }}>
                  <div style={{ padding: "10px 12px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
                      <DocumentRegular style={{ fontSize: "13px", color: "#0B5D5D" }} />
                      <Text style={{ fontSize: "10px", letterSpacing: "0.6px", textTransform: "uppercase", color: "#0B5D5D", fontWeight: 700 }}>
                        Selected Tender
                      </Text>
                    </div>
                    <div>
                      <Tooltip content={selectedTender.title} relationship="label">
                        <Text
                          weight="bold"
                          style={{
                            fontSize: "14px",
                            color: "#124242",
                            display: "block",
                            lineHeight: "1.3",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis"
                          }}
                        >
                          {selectedTender.title}
                        </Text>
                      </Tooltip>
                      <Text style={{ fontSize: "11px", color: "#666", display: "block", marginTop: "2px" }}>
                        {selectedTender.contractType} • Created {formatDate(selectedTender.createdAt)}
                      </Text>
                    </div>
                  </div>
                </Card>

                {/* Submission Form */}
                <div ref={submissionFormRef}>
                  <EmailSubmissionForm
                    defaultTitle={emailData.subject}
                    defaultLetterDate={emailData.receivedDateTime}
                    defaultDirection={emailData.isSentFolder ? 'Sent' : 'Received'}
                    tenderTitle={selectedTender.title}
                    tenderBaseCollectionName={selectedTender.baseCollectionName}
                    companyName={validationResult.companyName}
                    senderName={emailData.senderName}
                    senderEmail={emailData.senderEmail}
                    recipientName={emailData.recipientName || "Unknown"}
                    recipientEmail={emailData.recipientEmail || ""}
                    emailBody={emailData.bodyHtml}
                    onSubmit={handleFormSubmit}
                    onCancel={handleBackToTenderSelection}
                    isSubmitting={isSending}
                    isCheckingConversation={isCheckingConversation}
                    conversationCheckComplete={conversationCheckComplete}
                    sendSuccess={sendSuccess}
                    sendError={sendError}
                    isDuplicateMessage={isDuplicateMessage}
                    isThreadMetadataMissing={!!threadMetadataError}
                    threadMetadataError={threadMetadataError}
                  />
                </div>

                {/* Email Card */}
                <Card className={styles.emailCard}>
                  {/* Body Preview Section */}
                  <div className={styles.cardSection} style={{ padding: "10px 12px" }}>
                    <div className={styles.sectionLabel}>
                      <MailRegular style={{ fontSize: "14px" }} />
                      Email Content Preview
                    </div>
                    <div className={styles.bodyPreview}>
                      <div
                        className={styles.bodyHtmlContainer}
                        dangerouslySetInnerHTML={{ __html: emailData.bodyHtml }}
                      />
                    </div>
                    <div className={styles.statsRow}>
                      <span className={styles.statBadge}>
                        {stripHtml(emailData.bodyHtml).split(/\s+/).filter(Boolean).length} words
                      </span>
                      <span className={styles.statBadge}>
                        {Math.ceil(stripHtml(emailData.bodyHtml).length / 1000)}KB
                      </span>
                    </div>
                  </div>
                </Card>

                {/* Conversation Check Loading */}
                {isCheckingConversation && (
                  <Card className={styles.emailCard} style={{ marginBottom: "12px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "12px", padding: "16px" }}>
                      <Spinner size="small" />
                      <Text>Checking conversation status...</Text>
                    </div>
                  </Card>
                )}

                {/* Conversation Prompt */}
                {conversationCheckComplete && !isCheckingConversation && (
                  <ConversationPrompt
                    isNewConversation={isNewConversation}
                    conversationLetters={conversationLetters}
                  />
                )}

                {/* Letter Timeline */}
                {validationResult?.companyName && selectedTender.baseCollectionName && (
                  <Card className={styles.emailCard} style={{ marginBottom: "12px" }}>
                    <LetterTimeline
                      key={`${selectedTender.baseCollectionName}-${timelineRefreshKey}`}
                      companyName={validationResult.companyName}
                      baseCollectionName={selectedTender.baseCollectionName}
                      onScrollToBottom={handleScrollToForm}
                    />
                  </Card>
                )}
              </>
            )}
          </>
        ) : null}
      </div>

      {/* Footer - Show refresh button only during tender selection */}
      {
        emailData && !isLoading && !error && !isValidating && validationResult?.success && showTenderSelection && !selectedTender && (
          <div className={styles.footer}>
            <Button
              appearance="secondary"
              icon={<ArrowSyncRegular />}
              iconPosition="after"
              onClick={loadEmailData}
              className={styles.secondaryButton}
            >
              Refresh Email
            </Button>
          </div>
        )
      }
    </div >
  );
};

export default App;
